import { useMemo, useState } from 'react'
import { api } from '../api.js'
import {
  Banner, Button, Card, ErrorState, Field, Loading, Metric, MetricRow, SectionHeader, StatusPill, Table,
} from '../components.jsx'
import { useApi } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * Preventive maintenance schedules — create, correct, retire.
 *
 * **This page is the answer to "we are saying no PM is due — where are we adding these?"** The answer was
 * nowhere. `POST /api/v1/pm-schedules` shipped admin-gated, working and test-covered with **no client
 * anywhere**: not in the Android Retrofit interface, not in this console, not in the legacy admin pages. On
 * production the `pm_schedule` table was empty and would have stayed empty forever, because the only two
 * schedules that exist anywhere are dev seed rows and seed data loads under the `dev` profile only. So the
 * engineer's Work tab said "Nothing due" and always would have.
 *
 * `AUDIT.md:188` had this listed under "whole modules with zero client presence" since July.
 *
 * Two endpoints were added for this page rather than worked around: `PATCH /{id}` and
 * `POST /{id}/deactivate`. Before them `active` and `deleted_at` were read by every query while nothing ever
 * wrote them, so a schedule with a mistyped interval could only be corrected with SQL against production.
 * `GET /pm-schedules` also required an `assetId`, which meant the plant's PM plan could not be listed at all —
 * only one crane at a time — so an admin could not find the schedule they needed to fix.
 */
export default function PlannedMaintenance() {
  const schedules = useApi('/pm-schedules')
  const assets = useApi('/assets')
  const due = useApi('/pm-schedules/due?withinDays=14')

  const [editing, setEditing] = useState(null)   // a schedule row, or 'new'
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)

  const rows = useMemo(() => (Array.isArray(schedules.data) ? schedules.data : []), [schedules.data])
  const fleet = useMemo(() => (Array.isArray(assets.data) ? assets.data : []), [assets.data])
  const dueRows = useMemo(() => (Array.isArray(due.data) ? due.data : []), [due.data])

  const craneOf = (id) => fleet.find((a) => a.id === id)

  async function run(fn, text) {
    setBusy(true)
    setNotice(null)
    try {
      await fn()
      // Reload rather than patch locally: the server owns nextDueAt, and after an interval change the
      // recomputed date is the whole point of the edit. Guessing it here would show a date nothing agrees with.
      schedules.reload()
      due.reload()
      setNotice({ kind: 'success', text })
      setEditing(null)
    } catch (e) {
      setNotice({ kind: 'danger', text: e.message })
    } finally {
      setBusy(false)
    }
  }

  const active = rows.filter((s) => s.active !== false)

  return (
    <>
      <PageHeader
        title="Planned maintenance"
        subtitle="Preventive maintenance schedules. Engineers complete these on the app; they are defined here."
        actions={<Button onClick={() => setEditing('new')}>New schedule</Button>}
      />

      {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}

      {schedules.data && rows.length === 0 && (
        <Banner kind="warning">
          <span>
            No PM schedules exist, so <strong>nothing is ever due</strong> and every engineer&apos;s Work tab
            reads &quot;Nothing due&quot;. Schedules are per-crane plant policy rather than a technical default,
            which is why none ship — add the plant&apos;s rounds below.
          </span>
        </Banner>
      )}

      {schedules.loading && <Loading rows={5} />}
      {schedules.error && <ErrorState error={schedules.error} onRetry={schedules.reload} />}

      {schedules.data && (
        <>
          <MetricRow>
            <Metric label="Schedules" value={rows.length} />
            <Metric label="Active" value={active.length} kind={active.length ? 'success' : 'warning'} />
            <Metric
              label="Retired"
              value={rows.filter((s) => s.active === false).length}
              hint="Kept — completed rounds reference them"
            />
            <Metric
              label="Due within 14 days"
              value={dueRows.length}
              kind={dueRows.some((d) => d.overdue) ? 'danger' : undefined}
              hint={dueRows.filter((d) => d.overdue).length ? `${dueRows.filter((d) => d.overdue).length} overdue` : undefined}
            />
            <Metric
              label="Cranes with no PM"
              value={fleet.filter((a) => a.status !== 'DECOMMISSIONED' && !rows.some((s) => s.assetId === a.id && s.active !== false)).length}
              hint="No planned round at all"
              kind="warning"
            />
          </MetricRow>

          <SectionHeader hint="Click a schedule to correct or retire it">Schedules</SectionHeader>
          <Card>
            <Table
              keyOf={(s) => s.id}
              onRowClick={(s) => setEditing(s)}
              columns={[
                { key: 'title', header: 'Round', render: (s) => <strong>{s.title}</strong> },
                {
                  key: 'crane',
                  header: 'Crane',
                  render: (s) => {
                    const a = craneOf(s.assetId)
                    if (!a) {
                      // A type-level schedule has no crane. PmService.due() excludes these and complete()
                      // refuses them, so saying so beats an empty cell that looks like missing data.
                      return <span className="muted">All of type · not offered as work</span>
                    }
                    return (
                      <span className="cell-stack">
                        <span>{a.assetCode}</span>
                        <span className="muted">{[a.department, a.bay || a.area].filter(Boolean).join(' · ')}</span>
                      </span>
                    )
                  },
                },
                { key: 'discipline', header: 'Discipline', render: (s) => titleCase(s.discipline) },
                {
                  key: 'trigger',
                  header: 'Trigger',
                  render: (s) => (s.triggerType === 'USAGE'
                    ? <span>Every {num(s.usageInterval)} {s.usageReadingDefCode || 'units'}</span>
                    : <span>Every {s.intervalDays} days</span>),
                },
                { key: 'lastDone', header: 'Last done', render: (s) => fmtDate(s.lastDoneAt) },
                {
                  key: 'nextDue',
                  header: 'Next due',
                  render: (s) => {
                    if (s.triggerType === 'USAGE') {
                      // A meter-driven round has no calendar date by design, so an em dash here is the truth
                      // rather than missing data.
                      return <span className="muted" title="Meter-driven — no calendar date">On meter</span>
                    }
                    if (!s.nextDueAt) return <span className="muted">—</span>
                    const overdue = new Date(s.nextDueAt) < new Date()
                    return <span className={overdue ? 'val-bad' : undefined}>{fmtDate(s.nextDueAt)}</span>
                  },
                },
                {
                  key: 'state',
                  header: 'State',
                  render: (s) => (s.active === false
                    ? <StatusPill status="CLOSED" title="Retired — not offered as work" />
                    : <StatusPill status="ACTIVE" />),
                },
              ]}
              rows={rows}
              empty={<p className="muted">No schedules yet.</p>}
            />
          </Card>
        </>
      )}

      {editing && (
        <ScheduleForm
          schedule={editing === 'new' ? null : editing}
          fleet={fleet}
          busy={busy}
          onClose={() => setEditing(null)}
          onSubmit={(body) => {
            const isNew = editing === 'new'
            run(
              () => api(isNew ? '/pm-schedules' : `/pm-schedules/${editing.id}`, {
                method: isNew ? 'POST' : 'PATCH',
                body,
              }),
              isNew ? `Created "${body.title}".` : `"${body.title || editing.title}" updated.`,
            )
          }}
          onDeactivate={() => run(
            () => api(`/pm-schedules/${editing.id}/deactivate`, { method: 'POST', body: {} }),
            `"${editing.title}" retired.`,
          )}
        />
      )}
    </>
  )
}

/* ---- Create / edit ------------------------------------------------------- */

const DISCIPLINES = [
  { value: 'MECHANICAL', label: 'Mechanical' },
  { value: 'ELECTRICAL', label: 'Electrical' },
  { value: 'EITHER', label: 'Either — both disciplines see it' },
]

function ScheduleForm({ schedule, fleet, busy, onClose, onSubmit, onDeactivate }) {
  const isNew = !schedule
  const [f, setF] = useState({
    assetId: schedule?.assetId || '',
    title: schedule?.title || '',
    discipline: schedule?.discipline || 'MECHANICAL',
    triggerType: schedule?.triggerType || 'INTERVAL',
    intervalDays: schedule?.intervalDays ?? 30,
    usageReadingDefCode: schedule?.usageReadingDefCode || 'operating_hours',
    usageInterval: schedule?.usageInterval ?? '',
    lastDoneAt: schedule?.lastDoneAt ? schedule.lastDoneAt.slice(0, 10) : '',
  })
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })

  const isUsage = f.triggerType === 'USAGE'
  const canSubmit = f.title.trim() && (isNew ? f.assetId : true) &&
    (isUsage ? f.usageInterval && f.usageReadingDefCode : Number(f.intervalDays) > 0)

  function submit(e) {
    e.preventDefault()
    // A date input gives YYYY-MM-DD; the server wants an OffsetDateTime. Sent as start-of-day in the
    // browser's zone rather than UTC midnight, which would land on the previous day for a plant at +05:30.
    const lastDone = f.lastDoneAt ? new Date(`${f.lastDoneAt}T00:00:00`).toISOString() : null

    if (isNew) {
      onSubmit({
        assetId: f.assetId,
        title: f.title.trim(),
        discipline: f.discipline,
        triggerType: f.triggerType,
        intervalDays: isUsage ? null : Number(f.intervalDays),
        usageReadingDefCode: isUsage ? f.usageReadingDefCode : null,
        usageInterval: isUsage ? Number(f.usageInterval) : null,
        lastDoneAt: lastDone,
      })
    } else {
      // PATCH: only what an edit may change. assetId and triggerType are absent deliberately — re-pointing a
      // schedule at another crane would silently reattribute its completion history, and switching trigger
      // would leave the other trigger's fields describing a rule no longer in force.
      onSubmit({
        title: f.title.trim(),
        discipline: f.discipline,
        intervalDays: isUsage ? null : Number(f.intervalDays),
        usageReadingDefCode: isUsage ? f.usageReadingDefCode : null,
        usageInterval: isUsage ? Number(f.usageInterval) : null,
        lastDoneAt: lastDone,
      })
    }
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <form className="sheet" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <header className="sheet-head">
          <h2>{isNew ? 'New PM schedule' : `Edit ${schedule.title}`}</h2>
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </header>

        <div className="sheet-body">
          <div className="grid-2">
            <Field label="Round" id="pt" hint="What the engineer will see, e.g. 'Monthly lubrication'">
              <input id="pt" value={f.title} onChange={set('title')} required autoFocus />
            </Field>

            <Field
              label="Crane"
              id="pa"
              hint={isNew ? 'Which machine this round is for' : 'Cannot change — retire and create instead'}
            >
              <select id="pa" value={f.assetId} onChange={set('assetId')} disabled={!isNew} required={isNew}>
                <option value="">Select…</option>
                {fleet
                  .filter((a) => a.status !== 'DECOMMISSIONED')
                  .map((a) => <option key={a.id} value={a.id}>{a.assetCode} — {a.name}</option>)}
              </select>
            </Field>

            <Field label="Discipline" id="pd" hint="Who gets the round in their due list">
              <select id="pd" value={f.discipline} onChange={set('discipline')}>
                {DISCIPLINES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </Field>

            <Field
              label="Trigger"
              id="pg"
              hint={isNew ? 'Calendar interval, or driven by a meter reading' : 'Cannot change after creation'}
            >
              <select id="pg" value={f.triggerType} onChange={set('triggerType')} disabled={!isNew}>
                <option value="INTERVAL">Interval — every N days</option>
                <option value="USAGE">Usage — every N meter units</option>
              </select>
            </Field>

            {isUsage ? (
              <>
                <Field label="Reading" id="pu" hint="The meter that drives it, e.g. operating_hours">
                  <input id="pu" value={f.usageReadingDefCode} onChange={set('usageReadingDefCode')} required />
                </Field>
                <Field label="Every" id="pi" hint="Units between rounds">
                  <input id="pi" type="number" min="1" step="any" value={f.usageInterval} onChange={set('usageInterval')} required />
                </Field>
              </>
            ) : (
              <Field label="Every (days)" id="pn">
                <input id="pn" type="number" min="1" value={f.intervalDays} onChange={set('intervalDays')} required />
              </Field>
            )}

            <Field
              label="Last done"
              id="pl"
              hint="The due date is counted from here. Leave blank and it counts from today."
            >
              <input id="pl" type="date" value={f.lastDoneAt} onChange={set('lastDoneAt')} />
            </Field>
          </div>

          {isUsage && (
            <Banner kind="info">
              A usage round has no calendar due date — it becomes due when the meter has advanced by the
              interval since the last completion. It needs at least one recorded reading for that meter before
              it can ever be due.
            </Banner>
          )}

          {f.discipline === 'EITHER' && (
            <Banner kind="info">
              Both engineering disciplines will see this round. A safety officer will not — preventive
              maintenance is engineering work.
            </Banner>
          )}

          {!isNew && schedule.active !== false && (
            <>
              <SectionHeader>Retire</SectionHeader>
              <p className="muted">
                Retiring stops the round being offered and keeps the record. There is no delete: every completed
                round references the schedule it was measured against, so removing it would leave that history
                pointing at nothing.
              </p>
              <Button type="button" variant="danger" disabled={busy} onClick={onDeactivate}>
                Retire {schedule.title}
              </Button>
            </>
          )}
        </div>

        <footer className="sheet-foot">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy || !canSubmit}>
            {busy ? 'Saving…' : isNew ? 'Create schedule' : 'Save changes'}
          </Button>
        </footer>
      </form>
    </div>
  )
}

const titleCase = (s) => (s ? s.charAt(0) + s.slice(1).toLowerCase() : '—')
const num = (v) => (v === null || v === undefined ? '—' : Number(v))

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}
